import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import InfoIcon from "@mui/icons-material/Info";
import InputIcon from "@mui/icons-material/Input";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import MemoryIcon from "@mui/icons-material/Memory";
import MonitorIcon from "@mui/icons-material/Monitor";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  ButtonProps,
  Divider,
  Grid2,
  InputAdornment,
  Paper,
  Portal,
  Stack,
  Tab,
  Tabs,
  TextField,
  TextFieldProps,
  Typography,
  TypographyProps,
} from "@mui/material";
import { AxiosResponse } from "axios";
import { ApiError } from "types/apiError";
import { apiFieldError } from "utils/helpers";
import { RevivnApiForm } from "components/Form/RevivnApiForm";
import { WarehouseLocationLink } from "components/Locations/WarehouseLocationLink";
import { TabPanel } from "components/Tabs/TabPanel";
import InventoryPartsBulkInbound from "components/TechnicianInventory/InventoryPartsBulkInbound";
import InventoryPartsPanel from "components/TechnicianInventory/InventoryPartsPanel";
import TechnicianInventoryImages from "components/TechnicianInventory/TechnicianInventoryImages";
import { ChangeLocationButton } from "components/TechnicianInventory/v2/InventoryChangeLocationButton";
import { InventoryCreateInternalITInventoryButton } from "components/TechnicianInventory/v2/InventoryCreateInternalITInventoryButton";
import { DeviceWipeStatus } from "components/TechnicianInventory/v2/InventoryDeviceWipeStatus";
import { InventoryEventsHistoryButton } from "components/TechnicianInventory/v2/InventoryEventHistoryButton";
import { PrintLabelButton } from "components/TechnicianInventory/v2/InventoryPrintLabelButton";
import { ViewPickupButton } from "components/TechnicianInventory/v2/InventoryViewPickupButton";
import {
  TechnicianFormFunctionalityAttributes,
  TechnicianFormHardwareAttributes,
} from "components/TechnicianInventory/v2/TechForm";
import { TechnicianFormSchema } from "components/TechnicianInventory/ValidationSchema";
import { Field, Form, Formik, FormikProps, useFormikContext } from "formik";
import { useAlert } from "hooks/useAlert";
import { useConfirmation } from "hooks/useConfirmation";
import useCurrentUser from "hooks/useCurrentUser";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import useInventory from "hooks/useInventory";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Inventory } from "types/inventory";
import { userHasPermission } from "utils/userPermissions";
import * as Yup from "yup";
import { SdStorageOutlined } from "@mui/icons-material";
import { SerialVerificationNotice } from "components/Inventories/SerialVerificationNotice";
import { useClearSerialVerification } from "components/Inventories/useClearSerialVerification";
import { InventoryDrivesGrid } from "components/TechnicianInventory/InventoryDrivesGrid";
import { SaveAndMoveDialog } from "components/TechnicianInventory/v2/SaveAndMoveDialog";

// Declared outside the page so a later render does not remount the input and drop the selection
// the next scan needs to replace.
function InventorySearchTextField({
  inventoryId,
  focusTarget,
  isDirty,
  onSearch,
  ...props
}: TextFieldProps & {
  inventoryId?: string;
  focusTarget: React.RefObject<HTMLInputElement | null>;
  isDirty: () => boolean;
  onSearch: (searchValue: string) => void;
}) {
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => event.target.select();
  const handleSubmit = (values: { searchValue: string }) => {
    if (!values.searchValue) return;
    if (isDirty()) {
      const prompt = window.confirm("You have unsaved changes. Are you sure you want to change inventories?");
      if (!prompt) return;
    }
    onSearch(values.searchValue);
  };

  return (
    <Formik initialValues={{ searchValue: inventoryId ?? "" }} enableReinitialize onSubmit={handleSubmit}>
      <Form>
        <Field
          {...props}
          inputRef={focusTarget}
          as={TextField}
          fullWidth
          name="searchValue"
          placeholder={inventoryId ?? "Scan or type ID"}
          onFocus={handleFocus}
          onBlur={handleSubmit}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Form>
    </Formik>
  );
}

export default function TechnicianInventoryPage() {
  const navigate = useNavigate();
  const { inventory, setInventory, getInventoryById } = useInventory();
  const { confirm } = useConfirmation();
  const { alertSuccess, alertError } = useAlert();
  const { enabled: inventoryPartsEnabled } = useFeatureFlag("inventory_parts");
  const [inboundPartsMode, setInboundPartsMode] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const techForm = useRef<FormikProps<{ inventory: Inventory }>>(null);
  const focusTarget = useRef<HTMLInputElement>(null);
  const moveAfterSave = useRef(false);
  const focusInventoryId = useRef(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [serialCheckCleared, setSerialCheckCleared] = useState(false);
  const { clear: confirmSerial, confirming: confirmingSerial } = useClearSerialVerification(inventory?.id, () => {
    setSerialCheckCleared(true);
  });
  const container = useRef<HTMLDivElement>(null);
  const { currentUserPermissions } = useCurrentUser();
  const isWarehouseManager = userHasPermission("warehouse_manager", currentUserPermissions);
  const params = useParams();

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      const typeIndex = ["inventory_details", "top_case", "board", "lcd", "battery", "drives"];

      if (!inventory) return;
      if (newValue === 0) navigate(`/technician/${inventory.id}`);
      else {
        const location = `/technician/${inventory.id}/inventory-parts/${typeIndex[newValue]}`;
        navigate(location, { replace: true });
      }
      setTabIndex(newValue);
    },
    [inventory, navigate],
  );

  const InboundInventoryPartsButton = (props: ButtonProps) => {
    const handleClick = () => setInboundPartsMode((prevState) => !prevState);
    const label = inboundPartsMode ? "Back" : "Inbound Parts";

    return (
      <Button {...props} onClick={handleClick}>
        {label}
      </Button>
    );
  };

  const InventoryDetails = () => {
    const { values } = useFormikContext<{ inventory: Inventory }>();

    const AttributeLabel = (props: TypographyProps & { label: string; value: React.ReactNode }) => (
      <Typography {...props}>
        {props.label}:{" "}
        <Typography component="span" color="primary">
          {props.value}
        </Typography>
      </Typography>
    );

    return (
      <Grid2 container size={{ xs: 12, md: "grow" }} columnSpacing={2}>
        <Grid2 size="auto">
          <AttributeLabel label="ID" value={values.inventory.id} />
          <AttributeLabel label="Label" value={values.inventory.label} />
        </Grid2>

        <Grid2 size="auto">
          <AttributeLabel
            label="Location"
            value={
              <WarehouseLocationLink
                code={values.inventory.warehouseLocation}
                locationId={values.inventory.locationId}
              />
            }
          />
          <AttributeLabel label="Warehouse" value={values.inventory.warehouse?.name} />
        </Grid2>

        <Grid2 size="auto">
          <AttributeLabel label="State" value={values.inventory.state} />
          <AttributeLabel label="Group" value={values.inventory.inventoryGroup} />
        </Grid2>
      </Grid2>
    );
  };

  const TechnicianFormSubmitButton = () => {
    const { dirty, isValid, isSubmitting, submitForm } = useFormikContext<{ inventory: Inventory }>();

    const handleSaveAndMove = async () => {
      moveAfterSave.current = true;
      try {
        await submitForm();
      } catch {
        // A 422 skip confirmation throws after it opens. The dialog is already on screen.
      } finally {
        // Success and a skip confirmation clear this flag. A failed validation does not.
        if (moveAfterSave.current) moveAfterSave.current = false;
      }
    };

    return (
      <Stack direction="row" spacing={1} sx={{ my: 2 }}>
        <Button
          fullWidth
          type="submit"
          color="primary"
          size="large"
          variant="contained"
          disabled={!dirty || !isValid || isSubmitting}
        >
          Save Inventory
        </Button>
        <Button
          fullWidth
          type="button"
          color="primary"
          size="large"
          variant="contained"
          disabled={!isValid || isSubmitting}
          onClick={() => {
            void handleSaveAndMove();
          }}
        >
          Save & Move
        </Button>
      </Stack>
    );
  };

  const handleSubmitSuccess = (response: AxiosResponse<Inventory>) => {
    const savedInventoryData: Inventory = response.data;
    const openMoveDialog = moveAfterSave.current;
    moveAfterSave.current = false;
    setInventory(savedInventoryData);
    techForm.current?.resetForm({ values: { inventory: savedInventoryData } });
    alertSuccess("Inventory saved successfully.");
    if (openMoveDialog) {
      setMoveDialogOpen(true);
      return;
    }
    focusInventoryId.current = true;
  };

  const handleSubmitError = (error: ApiError) => {
    const continueToMove = moveAfterSave.current;
    moveAfterSave.current = false;
    if (error.response?.status === 422) {
      const skipParam = apiFieldError(error, "skipParam");
      confirm({
        handler: () => {
          moveAfterSave.current = continueToMove;
          techForm.current?.setFieldValue("inventory." + skipParam, true, false);
          techForm.current?.handleSubmit();
        },
        message: apiFieldError(error, "message"),
        context: skipParam,
      });
    } else alertError("Failed to save inventory.");
    throw error;
  };

  useEffect(() => {
    setSerialCheckCleared(false);
  }, [inventory?.id]);

  const handleMoveDialogClose = () => {
    focusInventoryId.current = true;
    setMoveDialogOpen(false);
  };

  const handleMoved = async (code: string, locationId: number | null) => {
    if (!inventory) return;
    const refreshed = await getInventoryById(String(inventory.id));
    if (refreshed) {
      techForm.current?.resetForm({ values: { inventory: refreshed } });
    } else {
      const current = techForm.current?.values.inventory ?? inventory;
      // The check already named the stored location. Drop the previous id so the new code does not
      // link there when the refetch fails.
      const updated = { ...current, warehouseLocation: code, locationId: locationId ?? undefined };
      setInventory(updated);
      techForm.current?.resetForm({ values: { inventory: updated } });
    }
    focusInventoryId.current = true;
    setMoveDialogOpen(false);
  };

  // Dialog close and the save alert both render before the ID field can take focus. Select on the
  // next frame, and keep the request armed across any render that lands before that frame.
  useEffect(() => {
    if (!focusInventoryId.current || moveDialogOpen) return;
    const frame = requestAnimationFrame(() => {
      const input = focusTarget.current;
      if (!input) return;
      input.focus();
      input.select();
      focusInventoryId.current = false;
    });
    return () => cancelAnimationFrame(frame);
  });

  return (
    <>
      <Grid2 container marginBottom={1} spacing={1} justifyContent="flex-end" alignItems="center">
        <Grid2 size="grow">
          <InventorySearchTextField
            inventoryId={params.inventoryId}
            focusTarget={focusTarget}
            isDirty={() => Boolean(techForm.current?.dirty)}
            onSearch={(searchValue) => {
              // useInventory refetches only when params.inventoryId changes, so clearing
              // on a repeat of the current id would unmount the form until a new id is searched.
              if (searchValue === params.inventoryId) return;
              setInventory(undefined);
              navigate(`/technician/${searchValue}`);
            }}
            disabled={inboundPartsMode}
            variant="outlined"
            label="Inventory ID"
            placeholder="Enter ID or label"
          />
        </Grid2>
        <Grid2 hidden={!!inventory}>
          <InboundInventoryPartsButton size="large" variant="outlined" startIcon={<InputIcon />} />
        </Grid2>
      </Grid2>

      {inboundPartsMode && <InventoryPartsBulkInbound />}

      {inventory && (
        <>
          <RevivnApiForm
            action={`/inventories/${inventory?.id}`}
            method="PUT"
            initialValues={{ inventory: inventory }}
            validationSchema={Yup.object({ inventory: TechnicianFormSchema })}
            onSuccess={handleSubmitSuccess}
            onError={handleSubmitError}
            onSubmit={(data) => {
              // The flag is cleared by its own request. Leaving it in this save would turn it back on.
              delete data.inventory.serialVerificationRequired;
            }}
            enableReinitialize={true}
            FormikProps={{
              innerRef: techForm,
              validateOnChange: false,
              validateOnBlur: true,
            }}
          >
            <SerialVerificationNotice
              required={Boolean(inventory.serialVerificationRequired) && !serialCheckCleared}
              confirming={confirmingSerial}
              onConfirm={() => {
                void confirmSerial();
              }}
            />
            <Grid2 container marginBottom={1} rowSpacing={1}>
              <InventoryDetails />
              <Grid2 sx={{ alignSelf: "center" }}>
                <DeviceWipeStatus />
              </Grid2>
              <Grid2>
                <ViewPickupButton />
              </Grid2>
              <Grid2>
                <ChangeLocationButton />
              </Grid2>
              <Grid2>
                <PrintLabelButton inventory={inventory} />
              </Grid2>
              <Grid2>
                <InventoryEventsHistoryButton inventory={inventory} />
              </Grid2>
              {isWarehouseManager && !inventory.pickup.internalItAssetsPickup && (
                <Grid2>
                  <InventoryCreateInternalITInventoryButton inventory={inventory} />
                </Grid2>
              )}
            </Grid2>
            <Grid2 container columns={2} spacing={{ sm: 1, md: 2 }}>
              <Grid2 size={{ sm: 2, md: 1 }} component={Paper} variant="outlined" padding={2}>
                <Grid2 sx={{ paddingBottom: 1 }}>
                  <Typography variant="h6">Tech Form</Typography>
                </Grid2>
                <Grid2 container columns={2} spacing={2}>
                  <TechnicianFormFunctionalityAttributes />
                </Grid2>
              </Grid2>

              <Grid2 size={{ sm: 2, md: 1 }} sx={{ padding: 2 }} component={Paper} variant="outlined">
                {inventoryPartsEnabled && (
                  <Tabs
                    scrollButtons
                    allowScrollButtonsMobile
                    onChange={handleTabChange}
                    variant="scrollable"
                    value={tabIndex}
                  >
                    <Tab icon={<InfoIcon />} label="Details" value={0} />
                    <Tab icon={<KeyboardIcon />} label="Case" value={1} />
                    <Tab icon={<MemoryIcon />} label="Board" value={2} />
                    <Tab icon={<MonitorIcon />} label="LCD" value={3} />
                    <Tab icon={<BatteryChargingFullIcon />} label="Battery" value={4} />
                    <Tab icon={<SdStorageOutlined />} label="Drives" value={5} />
                  </Tabs>
                )}
                <Divider sx={{ mb: 1 }} />
                <TabPanel index={0} value={tabIndex}>
                  <Stack spacing={1}>
                    <TechnicianFormHardwareAttributes />
                    <Typography variant="h6">Photos</Typography>
                    <TechnicianInventoryImages />
                    <TechnicianFormSubmitButton />
                  </Stack>
                </TabPanel>
                <Box ref={container} />
              </Grid2>
            </Grid2>
          </RevivnApiForm>
          <Portal container={() => container.current!}>
            <TabPanel index={1} value={tabIndex}>
              <InventoryPartsPanel partType="TopCase" parentInventory={inventory} />
            </TabPanel>
            <TabPanel index={2} value={tabIndex}>
              <InventoryPartsPanel partType="Board" parentInventory={inventory} />
            </TabPanel>
            <TabPanel index={3} value={tabIndex}>
              <InventoryPartsPanel partType="Lcd" parentInventory={inventory} />
            </TabPanel>
            <TabPanel index={4} value={tabIndex}>
              <InventoryPartsPanel partType="Battery" parentInventory={inventory} />
            </TabPanel>
            <TabPanel index={5} value={tabIndex}>
              <InventoryDrivesGrid inventory={inventory} />
            </TabPanel>
          </Portal>
          <SaveAndMoveDialog
            open={moveDialogOpen}
            inventoryId={inventory.id}
            onClose={handleMoveDialogClose}
            onMoved={handleMoved}
          />
        </>
      )}
    </>
  );
}
